import { cloneElement, ReactElement, useEffect, useRef } from "react";
import {
  Button,
  Drawer as HeroUIDrawer,
  DrawerBody,
  DrawerContent,
  DrawerProps as HeroUIDrawerProps,
} from "@heroui/react";

import { DotsThreeVerticalIcon, IconProps } from "@phosphor-icons/react";
import { SCALE_IN_AND_OUT } from "@/config/constants";
import { useOpenState } from "@/shared/hooks/useOpenState";

interface DrawerProps extends HeroUIDrawerProps {
  icon?: ReactElement<IconProps>;
  id: string;
  triggerProps?: {
    isDisabled?: boolean;
  };
  externalState?: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  };
}

export function Drawer({ children, icon, id, style, triggerProps, externalState, ...rest }: DrawerProps) {
  const { isOpen, handleOpenChange } = useOpenState({ id, externalState });
  const iconElement = icon ? (
    cloneElement(icon, { size: 24, ...icon?.props })
  ) : (
    <DotsThreeVerticalIcon size={24} weight="bold" />
  );
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (drawerRef.current) {
      if (drawerRef.current.parentElement) {
        drawerRef.current.parentElement.style.height = "100dvh";
      }
    }
  }, [isOpen]);

  return (
    <>
      {externalState ? null : (
        <Button
          isDisabled={triggerProps?.isDisabled}
          isIconOnly
          size="sm"
          variant="light"
          onPress={() => handleOpenChange(!isOpen)}
        >
          {iconElement}
        </Button>
      )}
      <HeroUIDrawer
        className="z-500"
        hideCloseButton
        isOpen={isOpen}
        motionProps={SCALE_IN_AND_OUT}
        placement="bottom"
        ref={drawerRef}
        style={{
          maxHeight: "calc(var(--viewport-height) - var(--inset-top))",
          position: "fixed",
          bottom: "var(--keyboard-height)",
          paddingBottom: "var(--inset-bottom)",
          ...style,
        }}
        onOpenChange={handleOpenChange}
        {...rest}
      >
        <DrawerContent>
          <DrawerBody className="p-4">{children}</DrawerBody>
        </DrawerContent>
      </HeroUIDrawer>
    </>
  );
}
